// src/pages/HelpPage.jsx
//
// /help — the "what is this tool and what is on each page" index. Complements
// components/HelpDrawer.jsx (per-page, opened from the nav's "?"): this is the
// map you read when you do not yet know which page you need.
//
// Three parts: the orientation note (the scope question that explains most
// "this page looks empty" confusion), the "which page do I want?" router, and
// every page's entry grouped by nav section. All copy comes from
// src/help/pageHelp.js — no second source of truth.

import React, { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import Box from '@mui/material/Box';
import Paper from '@mui/material/Paper';
import Typography from '@mui/material/Typography';
import Chip from '@mui/material/Chip';
import TextField from '@mui/material/TextField';
import Accordion from '@mui/material/Accordion';
import AccordionSummary from '@mui/material/AccordionSummary';
import AccordionDetails from '@mui/material/AccordionDetails';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import WarningAmberOutlinedIcon from '@mui/icons-material/WarningAmberOutlined';
import { PAGE_HELP, PAGE_PICKER, HELP_GROUPS } from '../help/pageHelp';

const CARD_SX = {
  p: 2.5,
  mb: 2.5,
  bgcolor: 'var(--card)',
  border: '1px solid var(--border)',
  backgroundImage: 'none',
};

function matches(entry, q) {
  if (!q) return true;
  const hay = [entry.label, entry.what, entry.useWhen, ...(entry.steps || []), ...(entry.gotchas || [])]
    .join(' ')
    .toLowerCase();
  return hay.includes(q.toLowerCase());
}

export default function HelpPage() {
  const [q, setQ] = useState('');

  const grouped = useMemo(() => {
    const hits = PAGE_HELP.filter((e) => matches(e, q));
    return Object.keys(HELP_GROUPS)
      .map((g) => ({ group: g, title: HELP_GROUPS[g], entries: hits.filter((e) => e.group === g) }))
      .filter((s) => s.entries.length > 0);
  }, [q]);

  return (
    <Box sx={{ p: 3, maxWidth: 1000, mx: 'auto' }}>
      <Typography variant="h5" sx={{ color: 'var(--ink)', mb: 0.5 }}>
        How to use AdLens FX
      </Typography>
      <Typography sx={{ fontSize: 13.5, color: 'var(--muted)', mb: 3, lineHeight: 1.6 }}>
        Every page reads from one place: the traffic the proxy captured while your device was
        pointed at it. So the first question on any page is always <i>is there traffic in scope?</i>
        {' '}Press the <b>?</b> in the top bar on any page for its help without coming back here.
      </Typography>

      {/* Orientation — the scope question, which explains most "this page is empty" reports. */}
      <Paper sx={CARD_SX} elevation={0}>
        <Typography sx={{ fontSize: 11, fontWeight: 700, letterSpacing: 0.6, color: 'var(--muted)', mb: 1.5 }}>
          BEFORE YOU START
        </Typography>
        <Box component="ol" sx={{ m: 0, pl: 2.5, color: 'var(--ink)', fontSize: 13, lineHeight: 1.9 }}>
          <li>
            Point the device at the proxy — <Link to="/admin" style={{ color: '#4fb3bc' }}>Admin Panel</Link> has
            the QR code and the iOS profile — and confirm it shows as allowed.
          </li>
          <li>
            Exercise the app. The header strip should show the request count climbing. If it is not,
            nothing downstream will work.
          </li>
          <li>
            Optionally select rows on the <Link to="/" style={{ color: '#4fb3bc' }}>Dashboard</Link> and hand
            them off ("Run checks"). Pages that accept a selection use it as their scope; with no
            selection they use the whole session.
          </li>
        </Box>
        <Typography sx={{ fontSize: 12.5, color: 'var(--muted)', mt: 1.5, lineHeight: 1.6 }}>
          Pages marked <b>Works standalone</b> below need no capture at all — paste something in and read
          the result. AI-backed features (check compile, AC compile, bug triage) need an Anthropic API key
          on the proxy; without one they fall back to deterministic behaviour and say so inline.
        </Typography>
      </Paper>

      {/* Which page do I want? */}
      <Paper sx={CARD_SX} elevation={0}>
        <Typography sx={{ fontSize: 11, fontWeight: 700, letterSpacing: 0.6, color: 'var(--muted)', mb: 1.5 }}>
          WHICH PAGE DO I WANT?
        </Typography>
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' },
            columnGap: 3,
            rowGap: 0.75,
          }}
        >
          {PAGE_PICKER.map(([question, path]) => {
            const entry = PAGE_HELP.find((e) => e.path === path);
            return (
              <Box
                key={`${question}-${path}`}
                sx={{ display: 'flex', gap: 1, alignItems: 'baseline', fontSize: 12.5 }}
              >
                <Typography sx={{ flex: 1, fontSize: 12.5, color: 'var(--muted)' }}>{question}</Typography>
                <Link to={path} style={{ color: '#4fb3bc', textDecoration: 'none', fontWeight: 600 }}>
                  {entry ? entry.label : path}
                </Link>
              </Box>
            );
          })}
        </Box>
      </Paper>

      <TextField
        size="small"
        fullWidth
        value={q}
        onChange={(e) => setQ(e.target.value)}
        placeholder="Search the pages — try &quot;consent&quot;, &quot;impression&quot;, &quot;sign off&quot;…"
        sx={{ mb: 2 }}
      />

      {grouped.length === 0 && (
        <Typography sx={{ fontSize: 13, color: 'var(--muted)' }}>Nothing matches “{q}”.</Typography>
      )}

      {grouped.map((section) => (
        <Box key={section.group} sx={{ mb: 3 }}>
          <Typography
            sx={{ fontSize: 11, fontWeight: 700, letterSpacing: 0.6, color: 'var(--muted)', mb: 1 }}
          >
            {section.title.toUpperCase()}
          </Typography>

          {section.entries.map((e) => (
            <Accordion
              key={e.path}
              defaultExpanded={Boolean(q)}
              disableGutters
              elevation={0}
              sx={{
                bgcolor: 'var(--card)',
                border: '1px solid var(--border)',
                backgroundImage: 'none',
                mb: 1,
                '&:before': { display: 'none' },
              }}
            >
              <AccordionSummary expandIcon={<ExpandMoreIcon sx={{ color: 'var(--muted)' }} />}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25, flexWrap: 'wrap', pr: 1 }}>
                  <Typography sx={{ fontSize: 14, fontWeight: 600, color: 'var(--ink)' }}>
                    {e.label}
                  </Typography>
                  <Chip
                    size="small"
                    label={e.needsTraffic ? 'Needs a live capture' : 'Works standalone'}
                    sx={{
                      height: 18,
                      fontSize: 10,
                      fontWeight: 600,
                      bgcolor: e.needsTraffic ? 'rgba(245,158,11,0.16)' : 'rgba(34,197,94,0.16)',
                      color: e.needsTraffic ? '#f59e0b' : '#22c55e',
                    }}
                  />
                  <Typography sx={{ fontSize: 12.5, color: 'var(--muted)', width: '100%' }}>
                    {e.what}
                  </Typography>
                </Box>
              </AccordionSummary>

              <AccordionDetails sx={{ pt: 0 }}>
                {e.useWhen && (
                  <Typography sx={{ fontSize: 12.5, color: 'var(--muted)', mb: 1.5, lineHeight: 1.6 }}>
                    <b style={{ color: 'var(--ink)' }}>Use it when: </b>
                    {e.useWhen}
                  </Typography>
                )}

                {e.steps?.length > 0 && (
                  <Box component="ol" sx={{ m: 0, pl: 2.5, mb: e.gotchas?.length ? 1.5 : 0 }}>
                    {e.steps.map((s, i) => (
                      <Box
                        key={i}
                        component="li"
                        sx={{ fontSize: 12.5, color: 'var(--ink)', lineHeight: 1.65, mb: 0.5 }}
                      >
                        {s}
                      </Box>
                    ))}
                  </Box>
                )}

                {e.gotchas?.map((g, i) => (
                  <Box key={i} sx={{ display: 'flex', gap: 1, mb: 0.75, alignItems: 'flex-start' }}>
                    <WarningAmberOutlinedIcon sx={{ fontSize: 15, color: '#f59e0b', mt: '2px' }} />
                    <Typography sx={{ fontSize: 12.5, color: 'var(--muted)', lineHeight: 1.6 }}>{g}</Typography>
                  </Box>
                ))}

                <Box sx={{ mt: 1.5 }}>
                  <Link to={e.path} style={{ color: '#4fb3bc', fontSize: 12.5, textDecoration: 'none' }}>
                    Open {e.label} →
                  </Link>
                </Box>
              </AccordionDetails>
            </Accordion>
          ))}
        </Box>
      ))}
    </Box>
  );
}
